import React, { PropsWithChildren, memo, useEffect } from 'react';
import { ObservabilityErrorBoundary } from '@leagueplatform/observability';
import { LeagueIntlProvider } from '@leagueplatform/locales';
import { ToastManager } from '@leagueplatform/toast-messages';
import { isNodeTestEnv } from '@leagueplatform/app-environment';
import {
  setConfig,
  getConfig,
  LeagueConfigError,
  ConfigStoreStatus,
  useConfigSelection,
  subscribeToConfig,
} from '@leagueplatform/config';
import type { LeagueConfig } from '@leagueplatform/config';
import { SessionTimeout } from '@leagueplatform/session-timeout';
import { StandaloneAuth } from '@leagueplatform/auth-standalone';
import { Auth, Implementation } from '@leagueplatform/auth';
import { LeagueThemeProvider } from './league-theme-provider';
import {
  queryClient,
  ReactQueryProviderWrapper,
} from './react-query-provider-wrapper';
import { PermissionsErrorModalProvider } from './permissions-error-modal/permissions-error-modal.provider';

/**
 * Clear the React Query cache whenever the tenant identity changes mid-session.
 * This prevents components from rendering stale data from a previous tenant
 * while fresh queries are in flight.
 */
subscribeToConfig(
  ({ config }) => ({
    clientId: config.core.clientId,
    tenantId: config.core.tenantId,
  }),
  (selection, prevSelection) => {
    if (!prevSelection || !selection) return;
    queryClient.clear();
  },
);

const defaultOnSessionExpire = () => {
  StandaloneAuth.client.logout({
    returnTo: window.location.origin,
  });
};

const defaultOnSessionExtend = () => {
  StandaloneAuth.client.getTokenSilently({
    cacheMode: 'off',
  });
};

const NO_CONFIG_ERROR =
  'Config was neither set before mounting `<LeagueProvider>` nor passed to its `config` prop. You must do one or the other!';

/**
 * Everything `LeagueProvider` renders (various providers around the children), without the config logic.
 * Memoized in advance so that when it's rendered in `LeagueProvider` and the latter re-renders due
 * to a change in the `config` prop, none of the internals need to re-render.
 */
const InnerProviders = memo(({ children }: PropsWithChildren<{}>) => {
  const {
    sessionTimeoutInterval,
    onSessionExpire,
    onSessionExtend,
    forceIdleSessionLogout,
  } = useConfigSelection(({ config }) => {
    const authConfig = config.core.auth;

    if (!authConfig || !('clientOptions' in authConfig)) {
      // we are not in Standalone Auth mode. Just return Infinity, we won't be using it anyway.
      return {
        sessionTimeoutInterval: Infinity,
        onSessionExpire: defaultOnSessionExtend,
        onSessionExtend: defaultOnSessionExtend,
        forceIdleSessionLogout: false,
      };
    }

    /**
     * We are in Standalone Auth mode.
     *
     * Convert idleSessionLifetimeHours into milliseconds so we can pass it to the
     * `<SessionTimeout>` component.
     */
    return {
      sessionTimeoutInterval: authConfig.idleSessionLifetimeHours * 3.6e6,
      onSessionExpire:
        authConfig.sessionTimeoutOptions?.onSessionExpire ||
        defaultOnSessionExpire,
      onSessionExtend:
        authConfig.sessionTimeoutOptions?.onSessionExtend ||
        defaultOnSessionExtend,
      forceIdleSessionLogout:
        authConfig.sessionTimeoutOptions?.forceIdleSessionLogout || false,
    };
  });

  const contentsWithoutErrorBoundary = (
    <LeagueThemeProvider>
      <LeagueIntlProvider>
        <ReactQueryProviderWrapper>
          <PermissionsErrorModalProvider>
            {children}
            {!isNodeTestEnv() && <ToastManager />}
            {!isNodeTestEnv() &&
              Auth.initialized &&
              Auth.implementation === Implementation.STANDALONE && (
                <SessionTimeout
                  timeoutInterval={sessionTimeoutInterval}
                  getIsAuthenticated={Auth.isAuthorized}
                  onSessionExpire={onSessionExpire}
                  onSessionExtend={onSessionExtend}
                  forceIdleSessionLogout={forceIdleSessionLogout}
                />
              )}
          </PermissionsErrorModalProvider>
        </ReactQueryProviderWrapper>
      </LeagueIntlProvider>
    </LeagueThemeProvider>
  );

  return isNodeTestEnv() ? (
    contentsWithoutErrorBoundary
  ) : (
    <ObservabilityErrorBoundary>
      {contentsWithoutErrorBoundary}
    </ObservabilityErrorBoundary>
  );
});

type LeagueProviderProps = PropsWithChildren<{
  /**
   * a {@link LeagueConfig `LeagueConfig`} object to set the global League Config.
   */
  config?: LeagueConfig;
  /**
   * Whether to treat the `config` object as an initial value only. Should be set
   * to `true` if you intend to use the `setConfig` function dynamically at a later
   * time, after the initial render of this component
   */
  treatConfigAsInitialOnly?: boolean;
}>;

/**
 * Top-level provider of core league configuration and features. Must be rendered
 * above any routes or components coming from the League SDK.
 */
export const LeagueProvider = ({
  children,
  config,
  treatConfigAsInitialOnly,
}: LeagueProviderProps) => {
  if (getConfig(false).status === ConfigStoreStatus.Empty) {
    // the config has not yet been set.

    if (!config) {
      // AND we were not provided one via the prop.
      throw new LeagueConfigError(NO_CONFIG_ERROR);
    }

    /**
     * Set the config store for the first time immediately, so that descendents that
     * depend on the store having been set can be safely mounted.
     *
     * NOTE: we allow ourselves to mutate the config store as part of this
     * component's render (rather than only as an effect) even though doing so would
     * update the state of any components listening to the store, because
     * we know that by doing it **only if the store has not yet been set** means
     * that no components who listen to the store would currently be mounted.
     */
    setConfig(config);
  }

  useEffect(() => {
    /**
     * On any subsequent change to the `config` prop that happens **after**
     * the store was already set for the first time, we mutate the store as an effect,
     * in order to abide by React rules; specifically, that the state of one
     * component must never be updated as part of the rendering of another component.
     *
     * Note that we'll only do this if the `treatConfigAsInitialOnly` is not set
     * to `true`. If it is, we assume the consumer will not be changing the value
     * of this prop after the first render.
     */
    if (config && !treatConfigAsInitialOnly) {
      setConfig(config);
    }
  }, [config, treatConfigAsInitialOnly]);

  useEffect(
    () =>
      /**
       * Invalidate all queries when LeagueProvider unmounts to ensure fresh data
       * on remount. This prevents the UI from momentarily displaying stale content
       * (especially content from a previous locale) before new requests complete.
       */
      () => {
        queryClient.clear();
      },
    [],
  );

  return <InnerProviders>{children}</InnerProviders>;
};
